<template>
  <MembershipsSold v-if="!showAnyTabs && initialData" v-bind="initialData" />

  <Page v-else-if="initialData" title="Memberships">
    <TabSwitcher id="memberships" :tabs="tabs" />
  </Page>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref } from "vue";
import { useFeatureFlag } from "@/lib/useFeatureFlag";

import Page from "@/components/Page.vue";
import TabSwitcher from "@/components/TabSwitcher/TabSwitcher.vue";
import MembershipsSold from "@/Reports/MembershipsSold.vue";
import PaymentActivity from "@/Reports/PaymentActivity.vue";
import ServiceUtilization from "@/Reports/ServiceUtilization.vue";

// membership_tabs_enabled gates the tab chrome + Payment Activity tab.
// service_utilization_enabled gates the Service Utilization tab. Either
// flag alone is enough to show tabs so the two rollouts can ship
// independently; the tabs list inside filters itself per flag.
const paymentActivityEnabled = useFeatureFlag("membership_tabs_enabled");
const serviceUtilizationEnabled = useFeatureFlag("service_utilization_enabled");
const showAnyTabs = computed(
  () =>
    Boolean(paymentActivityEnabled.value) ||
    Boolean(serviceUtilizationEnabled.value)
);

const initialData = ref<{
  filters: [];
  source: string;
} | null>(null);

const failedPaymentsData = ref<{
  filters: [];
  source: string;
  failed_payments_count?: number;
} | null>(null);

const tabs = computed(() => [
  {
    name: "Membership To-Do",
    component: MembershipsSold,
    props: initialData.value
  },
  {
    name: "Payment Activity",
    component: PaymentActivity,
    props: {
      source: failedPaymentsData.value?.source || "",
      filters: failedPaymentsData.value?.filters || []
    },
    badge: failedPaymentsData.value?.failed_payments_count ?? null,
    visible: Boolean(paymentActivityEnabled.value)
  },
  {
    name: "Service Utilization",
    component: ServiceUtilization,
    visible: Boolean(serviceUtilizationEnabled.value)
  }
]);

const baseUrl = `${window.location.origin}${window.location.pathname}`;

onMounted(async () => {
  // failed_payments_initial_page_data 404s unless membership_tabs_enabled
  // is on, so skip that fetch when only service_utilization is enabled.
  const todoPromise = fetch(`${baseUrl}/initial_page_data`, { method: "GET" });
  const failedPromise = paymentActivityEnabled.value
    ? fetch(`${baseUrl}/failed_payments_initial_page_data`, { method: "GET" })
    : Promise.resolve(null);

  const [todoRes, failedRes] = await Promise.all([todoPromise, failedPromise]);
  initialData.value = await todoRes.json();
  if (failedRes) failedPaymentsData.value = await failedRes.json();
});
</script>
